// M009-F014: pulled out of EventSimpleDashboard.js (unchanged) so
// myBookingsGrouping.js can reuse them without importing the dashboard
// component itself (which would create a circular import — the dashboard
// imports the grouping module to build its sections). EventSimpleDashboard.js
// re-exports these by name, so every existing import of them from
// './EventSimpleDashboard' keeps working unchanged.
import { getNextOccurrence } from '../../utils/recurrenceParser';

// M007-F010 / Defect C: a booking is part of a recurring series if it is
// either the master ('recurring') or a materialised instance
// ('recurring_instance'), or carries any of the legacy recurrence markers.
// Used to keep the "recurring series" notice and the "locked" notice
// mutually exclusive (a series is never individually lockable).
export const isRecurringSeries = (booking) => Boolean(
  booking && (
    booking.booking_type === 'recurring'
    || booking.booking_type === 'recurring_instance'
    || booking.google_recurring_event_id
    || booking.recurring?.isRecurring
  ),
);

// Defect A: for a recurring master booking, the card must show the series'
// next occurrence (or, once the series has ended, its last occurrence) —
// never the raw anchor (DTSTART) date. Non-recurring bookings, and
// recurring rows we cannot compute an occurrence for (no RRULE), just show
// their own start/end untouched.
export const getDisplayOccurrence = (booking, now = new Date()) => {
  if (booking?.booking_type === 'recurring' && booking.google_recurrence_rule) {
    return getNextOccurrence(booking.google_recurrence_rule, booking.start_time, booking.end_time, now);
  }
  const start = new Date(booking?.start_time);
  const end = booking?.end_time ? new Date(booking.end_time) : null;
  return {
    start,
    end,
    isEnded: end ? end.getTime() < now.getTime() : start.getTime() < now.getTime(),
    isSeries: false,
  };
};

// Defect B (scoped 2026-09-04 per independent audit of !302): `status` and
// `approval_status` can disagree in production (booking 3218:
// status='approved', approval_status='pending'). The override below exists
// to correct exactly that shape of lie — a falsely confident 'approved'
// claim — NOT as a general "approval_status always wins" rule. Every other
// value of `status` (cancelled, rejected, delete_pending, ...) is the
// booking's real, already-decided disposition; a merely-lagging
// approval_status must never override it, or a dead booking (e.g.
// status='cancelled') would render as "⏳ Waiting for Approval", presenting
// it as still awaiting review. So the override only fires when `status`
// itself claims 'approved'.
export const resolveDisplayStatus = (booking) => {
  const status = booking?.status;
  const approvalStatus = booking?.approval_status;
  const statusClaimsApproved = status === 'approved';
  if (statusClaimsApproved && approvalStatus && approvalStatus !== status
      && (approvalStatus === 'pending' || approvalStatus === 'rejected')) {
    return { key: approvalStatus, disagreement: true, status, approvalStatus };
  }
  return { key: status, disagreement: false, status, approvalStatus };
};

// M004-F0074 / Defect C: Check if booking is locked (within 20 minutes of
// start). Bounded to the booking's own window: once its end_time has
// passed the booking is simply historical, not permanently "locked" — the
// previous `minutesUntilStart > -Infinity` lower bound was always true for
// any past start, so a recurring series anchored months ago never unlocked.
export const isBookingLocked = (booking, now = new Date()) => {
  if (!booking) return false;
  const bookingStart = new Date(booking.start_time);
  const bookingEnd = booking.end_time ? new Date(booking.end_time) : bookingStart;
  const minutesUntilStart = (bookingStart.getTime() - now.getTime()) / (1000 * 60);
  const hasEnded = !Number.isNaN(bookingEnd.getTime()) && now.getTime() > bookingEnd.getTime();
  return minutesUntilStart < 20 && !hasEnded;
};

// Defect B: takes the whole booking (not just booking.status) so it can
// honour resolveDisplayStatus's disagreement rule.
export const getStatusBadgeClass = (booking) => {
  const resolved = resolveDisplayStatus(booking);
  switch (resolved.key) {
    case 'approved': return 'status-badge success';
    case 'pending': return 'status-badge warning';
    case 'rejected': return 'status-badge error';
    case 'cancelled': return 'status-badge muted';
    default: return 'status-badge';
  }
};

// M004-F0074 / Defect B: Get status display text. When approval_status
// disagrees with status (e.g. status='approved', approval_status='pending'),
// resolveDisplayStatus resolves to approval_status so we never print a
// confident "Approved" badge over a row the database still considers
// pending or rejected.
export const getStatusText = (booking) => {
  const resolved = resolveDisplayStatus(booking);
  switch (resolved.key) {
    case 'approved': return '✓ Approved';
    case 'pending': return '⏳ Waiting for Approval';
    case 'rejected': return '✗ Rejected';
    case 'delete_pending': return '⏱️ Delete Pending';
    default: return resolved.key;
  }
};
